import React, { useEffect, useState } from 'react';
import { Button, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Moon, Sun } from 'lucide-react';

const THEME_STORAGE_KEY = 'tt-theme';

type ThemeMode = 'light' | 'dark';

function getSystemPreferred(): ThemeMode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getInitialTheme(): ThemeMode {
  const stored = (typeof localStorage !== 'undefined' && localStorage.getItem(THEME_STORAGE_KEY)) as ThemeMode | null;
  if (stored === 'light' || stored === 'dark') return stored;
  return getSystemPreferred();
}

const ThemeSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<ThemeMode>(getInitialTheme);

  // Apply to <html data-theme="...">
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    // Also toggle Mantine's dark class and attribute so Mantine components restyle
    root.classList.toggle('mantine-dark', theme === 'dark');
    root.setAttribute('data-mantine-color-scheme', theme);
    // Help native form controls pick appropriate default colors in each scheme
    if (root && root.style && typeof root.style.setProperty === 'function') {
      root.style.setProperty('color-scheme', theme);
    }
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // noop: ignore storage errors (e.g., privacy mode)
    }
  }, [theme]);

  const isDark = theme === 'dark';

  return (
    <Group gap="xs">
      <Button
        variant={isDark ? 'subtle' : 'filled'}
        size="xs"
        onClick={() => setTheme('light')}
        leftSection={<Sun size={14} />}
        aria-label={t('common.lightMode') || 'Light mode'}
      >
        {t('common.light') || 'Light'}
      </Button>
      <Button
        variant={isDark ? 'filled' : 'subtle'}
        size="xs"
        onClick={() => setTheme('dark')}
        leftSection={<Moon size={14} />}
        aria-label={t('common.darkMode') || 'Dark mode'}
      >
        {t('common.dark') || 'Dark'}
      </Button>
    </Group>
  );
};

export default ThemeSwitcher;
